/**
 * Header Glass Effect & Dynamic Color Detection
 * Add this script after your header.js or include in theme.js
 */

class HeaderGlassEffect {
  constructor() {
    this.header = document.querySelector('#header-component');
    if (!this.header) return;

    // Glass threshold
    this.glassThreshold = 50;
    
    this.lastScrollY = 0;
    this.ticking = false;
    this.currentTheme = null;
    this.glassEnabled = true;
    this.hasHeroSection = false;
    this.isMobile = window.innerWidth < 768;
    
    // Notch state tracking - default to hidden
    this.notchHidden = true;
    this.heroCopyElements = null;
    this.heroSection = null;
    this.opacityCheckRAF = null;
    
    this.init();
  }

  init() {
    // Clear initial state
    this.header.removeAttribute('data-scrolled');
    this.header.removeAttribute('data-glass-theme');
    
    // Set mobile attribute
    this.header.setAttribute('data-mobile', this.isMobile ? 'true' : 'false');
    
    // Check for hero section and set up watchers
    this.detectHeroSection();
    
    // Set initial notch state AFTER detecting hero section
    // For non-hero pages at scroll 0, show notch immediately
    const threshold = 10;
    if (!this.hasHeroSection && window.scrollY <= threshold) {
      this.notchHidden = false;
      this.header.removeAttribute('data-notch-hidden');
    } else {
      this.notchHidden = true;
      this.header.setAttribute('data-notch-hidden', 'true');
    }
    
    this.setupScrollDetection();
    this.setupResizeHandler();
    
    // Start watching for hero copy visibility
    this.startOpacityWatcher();
    
    // Check initial scroll position
    requestAnimationFrame(() => {
      this.handleScroll(window.scrollY);
      // Check if we should show notch on initial load
      this.checkInitialNotchState();
    });
  }

  checkInitialNotchState() {
    // Only show notch if:
    // 1. We have a hero section
    // 2. Hero is NOT in S3 state
    // 3. Hero copy is not visible
    
    if (!this.hasHeroSection || !this.heroSection) {
      // For pages WITHOUT hero section: show notch when at top (scroll <= 10px)
      const scrollY = window.scrollY;
      const threshold = 10;
      if (scrollY <= threshold) {
        this.notchHidden = false;
        this.header.removeAttribute('data-notch-hidden');
      } else {
        this.notchHidden = true;
        this.header.setAttribute('data-notch-hidden', 'true');
      }
      return;
    }
    
    // If already in S3 state, keep notch hidden
    if (this.heroSection.hasAttribute('data-s3')) {
      this.notchHidden = true;
      this.header.setAttribute('data-notch-hidden', 'true');
      return;
    }
    
    // Check if copy is visible
    if (this.heroCopyElements) {
      const { leftCopy, rightCopy } = this.heroCopyElements;
      let maxOpacity = 0;
      
      if (leftCopy) {
        maxOpacity = Math.max(maxOpacity, parseFloat(window.getComputedStyle(leftCopy).opacity) || 0);
      }
      if (rightCopy) {
        maxOpacity = Math.max(maxOpacity, parseFloat(window.getComputedStyle(rightCopy).opacity) || 0);
      }
      
      // If copy is visible, keep notch hidden
      if (maxOpacity > 0.05) {
        this.notchHidden = true;
        this.header.setAttribute('data-notch-hidden', 'true');
        return;
      }
    }
    
    // All conditions met - show notch
    this.notchHidden = false;
    this.header.removeAttribute('data-notch-hidden');
  }

  detectHeroSection() {
    this.heroSection = document.querySelector('[data-hero3d="true"]');
    
    this.hasHeroSection = !!this.heroSection;
    this.header.setAttribute('data-has-hero', this.hasHeroSection ? 'true' : 'false');
    
    if (!this.heroSection) {
      // No hero section - simple scroll-based notch control
      this.glassEnabled = true;
      // Notch state will be determined by scroll position (set in checkInitialNotchState)
      return;
    }

    // Find the hero copy elements
    this.heroCopyElements = {
      leftCopy: this.heroSection.querySelector('.leftCopy'),
      rightCopy: this.heroSection.querySelector('.rightCopy')
    };

    // Glass disabled until hero transitions (data-s3 is set)
    this.glassEnabled = this.heroSection.hasAttribute('data-s3');

    // Watch for changes - delay to not interfere with GSAP animations
    const observer = new MutationObserver(() => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.glassEnabled = this.heroSection.hasAttribute('data-s3');
          this.handleScroll(window.scrollY);
        }, 650);
      });
    });

    observer.observe(this.heroSection, { attributes: true, attributeFilter: ['data-s3'] });
  }

  startOpacityWatcher() {
    // Only start watcher if we have a hero section with copy elements
    if (!this.hasHeroSection || !this.heroCopyElements) return;
    if (this.isMobile) return;
    
    const checkOpacity = () => {
      const { leftCopy, rightCopy } = this.heroCopyElements;
      
      let maxOpacity = 0;
      
      if (leftCopy) {
        const opacity = parseFloat(window.getComputedStyle(leftCopy).opacity) || 0;
        maxOpacity = Math.max(maxOpacity, opacity);
      }
      
      if (rightCopy) {
        const opacity = parseFloat(window.getComputedStyle(rightCopy).opacity) || 0;
        maxOpacity = Math.max(maxOpacity, opacity);
      }
      
      // Check if hero is in S3 state (scrolled past the initial screens)
      const isInS3State = this.heroSection && this.heroSection.hasAttribute('data-s3');
      
      // Determine if notch should be hidden
      // Hide notch when:
      // 1. Copy becomes visible (opacity > 0.05)
      // 2. OR in S3 state
      const shouldHide = maxOpacity > 0.05 || isInS3State;
      
      // Update notch state
      if (shouldHide && !this.notchHidden) {
        this.notchHidden = true;
        this.header.setAttribute('data-notch-hidden', 'true');
      } 
      // Only show notch if:
      // 1. Copy is not visible
      // 2. NOT in S3 state
      else if (!shouldHide && this.notchHidden) {
        this.notchHidden = false;
        this.header.removeAttribute('data-notch-hidden');
      }
      
      // Continue checking
      this.opacityCheckRAF = requestAnimationFrame(checkOpacity);
    };
    
    // Start the continuous check loop
    this.opacityCheckRAF = requestAnimationFrame(checkOpacity);
  }

  stopOpacityWatcher() {
    if (this.opacityCheckRAF) {
      cancelAnimationFrame(this.opacityCheckRAF);
      this.opacityCheckRAF = null;
    }
  }

  setupScrollDetection() {
    window.addEventListener('scroll', () => {
      this.lastScrollY = window.scrollY;
      
      if (!this.ticking) {
        requestAnimationFrame(() => {
          this.handleScroll(this.lastScrollY);
          this.ticking = false;
        });
        this.ticking = true;
      }
    }, { passive: true });
  }

  setupResizeHandler() {
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        // Update mobile check
        const wasMobile = this.isMobile;
        this.isMobile = window.innerWidth < 768;
        
        // Update mobile attribute
        this.header.setAttribute('data-mobile', this.isMobile ? 'true' : 'false');
        
        // If mobile state changed, update scroll handling
        if (wasMobile !== this.isMobile) {
          // Restart watcher when switching between mobile/desktop
          this.stopOpacityWatcher();
          if (!this.isMobile && this.hasHeroSection) {
            // Reset notch state and check
            this.notchHidden = true;
            this.header.setAttribute('data-notch-hidden', 'true');
            this.startOpacityWatcher();
            this.checkInitialNotchState();
          } else if (!this.isMobile && !this.hasHeroSection) {
            // For non-hero pages, check scroll position
            this.checkInitialNotchState();
          }
          this.handleScroll(window.scrollY);
        } else if (this.header.getAttribute('data-scrolled') === 'true') {
          this.detectAndSetGlassTheme();
        }
      }, 100);
    }, { passive: true });
  }

  handleScroll(scrollY) {
    // Handle notch visibility for pages without hero section
    if (!this.hasHeroSection) {
      this.updateNotchForNonHeroPages(scrollY);
    }
    
    // Handle glass effect
    this.updateGlassState(scrollY);
  }

  updateNotchForNonHeroPages(scrollY) {
    // Simple scroll-based control for pages without hero
    // Show notch when at or very near top (within 10px), hide when scrolling down
    const threshold = 10;
    const shouldShowNotch = scrollY <= threshold;
    
    if (shouldShowNotch && this.notchHidden) {
      this.notchHidden = false;
      this.header.removeAttribute('data-notch-hidden');
    } else if (!shouldShowNotch && !this.notchHidden) {
      this.notchHidden = true;
      this.header.setAttribute('data-notch-hidden', 'true');
    }
  }

  updateGlassState(scrollY) {
    const isScrolled = scrollY > this.glassThreshold;
    const wasScrolled = this.header.getAttribute('data-scrolled') === 'true';
    
    // Skip glass effect on mobile
    if (this.isMobile) {
      if (wasScrolled) {
        this.header.setAttribute('data-scrolled', 'false');
        this.currentTheme = null;
      }
      return;
    }
    
    // Only show glass if enabled AND scrolled past threshold
    const shouldShowGlass = isScrolled && this.glassEnabled;
    
    if (shouldShowGlass) {
      // Delay glass effect until notch animation completes (600ms transition)
      if (!wasScrolled) {
        // Wait for notch to fully retract before showing glass
        setTimeout(() => {
          // Double-check we're still scrolled
          if (window.scrollY > this.glassThreshold) {
            this.header.setAttribute('data-scrolled', 'true');
            this.detectAndSetGlassTheme();
          }
        }, 400); // Match --notch-transition-speed
      } else {
        // Already in glass mode, just update theme
        this.detectAndSetGlassTheme();
      }
    } else {
      if (wasScrolled) {
        this.header.setAttribute('data-scrolled', 'false');
        this.currentTheme = null;
      }
    }
  }

  detectAndSetGlassTheme() {
    const headerRect = this.header.getBoundingClientRect();
    const headerHeight = headerRect.height;
    
    // Sample multiple points below the header for better detection
    const samplePoints = [
      { x: window.innerWidth * 0.25, y: headerHeight + 20 },
      { x: window.innerWidth * 0.5, y: headerHeight + 20 },
      { x: window.innerWidth * 0.75, y: headerHeight + 20 },
      { x: window.innerWidth * 0.5, y: headerHeight + 100 }
    ];
    
    let darkCount = 0;
    let lightCount = 0;
    
    for (const point of samplePoints) {
      const color = this.getBackgroundColorAtPoint(point.x, point.y);
      if (color) {
        if (this.isColorDark(color)) {
          darkCount++;
        } else {
          lightCount++;
        }
      }
    }
    
    // Determine theme based on majority
    const newTheme = darkCount > lightCount ? 'dark' : 'light';
    
    // Only update if theme changed
    if (newTheme !== this.currentTheme) {
      this.currentTheme = newTheme;
      this.header.setAttribute('data-glass-theme', newTheme);
    }
  }

  getBackgroundColorAtPoint(x, y) {
    const elements = document.elementsFromPoint(x, y);
    
    for (const el of elements) {
      // Skip header elements
      if (this.header.contains(el) || el === this.header) continue;
      // Skip html/body initially to check sections first
      if (el.tagName === 'HTML') continue;
      
      const color = this.extractBackgroundColor(el);
      if (color) return color;
    }
    
    // Fallback to body
    return this.extractBackgroundColor(document.body) || 'rgba(0, 0, 0, 0.4)';
  }

  extractBackgroundColor(element) {
    let el = element;
    let depth = 0;
    const maxDepth = 15;
    
    while (el && depth < maxDepth) {
      const style = window.getComputedStyle(el);
      
      // Check for background image first (videos, images, gradients)
      const bgImage = style.backgroundImage;
      if (bgImage && bgImage !== 'none') {
        // Check for gradient
        if (bgImage.includes('gradient')) {
          return this.extractGradientColor(bgImage);
        }
        // For images/videos, check for overlay or data attribute
        const overlayColor = this.checkOverlay(el);
        if (overlayColor) return overlayColor;
        
        // Check if element has a data attribute hint
        const themeHint = el.dataset.theme || el.dataset.colorScheme;
        if (themeHint) {
          return themeHint.includes('dark') ? 'rgb(0, 0, 0)' : 'rgb(255, 255, 255)';
        }
        
        // Default: assume images are dark (common for hero sections)
        return 'rgb(30, 30, 30)';
      }
      
      // Check background color
      const bgColor = style.backgroundColor;
      if (bgColor && !this.isTransparent(bgColor)) {
        return bgColor;
      }
      
      // Move to parent
      el = el.parentElement;
      depth++;
    }
    
    return null;
  }

  extractGradientColor(gradientStr) {
    // Extract first color from gradient
    const colorMatch = gradientStr.match(/rgb[a]?\([^)]+\)|#[0-9a-fA-F]{3,8}/);
    if (colorMatch) {
      return colorMatch[0];
    }
    return null;
  }

  checkOverlay(element) {
    const overlaySelectors = ['.overlay', '[class*="overlay"]', '.bg-overlay', '.section-overlay'];
    
    for (const selector of overlaySelectors) {
      try {
        const overlay = element.querySelector(selector);
        if (overlay) {
          const overlayBg = window.getComputedStyle(overlay).backgroundColor;
          if (overlayBg && !this.isTransparent(overlayBg)) {
            return overlayBg;
          }
        }
      } catch (e) {
        // Ignore invalid selectors
      }
    }
    
    // Check pseudo-elements
    const beforeBg = window.getComputedStyle(element, '::before').backgroundColor;
    if (beforeBg && !this.isTransparent(beforeBg)) return beforeBg;
    
    const afterBg = window.getComputedStyle(element, '::after').backgroundColor;
    if (afterBg && !this.isTransparent(afterBg)) return afterBg;
    
    return null;
  }

  isTransparent(color) {
    if (!color || color === 'transparent' || color === 'rgba(0, 0, 0, 0)') return true;
    
    const match = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+))?\s*\)/);
    if (match && match[4] !== undefined) {
      return parseFloat(match[4]) < 0.15;
    }
    
    return false;
  }

  isColorDark(colorStr) {
    if (!colorStr) return false;
    
    // Handle hex colors
    if (colorStr.startsWith('#')) {
      colorStr = this.hexToRgb(colorStr);
    }

    const rgb = colorStr.match(/[\d.]+/g);
    if (!rgb || rgb.length < 3) return false;

    const r = parseFloat(rgb[0]);
    const g = parseFloat(rgb[1]);
    const b = parseFloat(rgb[2]);
    const a = rgb.length > 3 ? parseFloat(rgb[3]) : 1;

    // If very transparent, consider it light
    if (a < 0.3) return false;

    // Calculate relative luminance (more accurate than simple average)
    // Using sRGB luminance formula
    const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;

    // Threshold at 0.5 (middle gray)
    return luminance < 0.5;
  }

  hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (result) {
      return `rgb(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)})`;
    }
    // Handle shorthand hex
    const shortResult = /^#?([a-f\d])([a-f\d])([a-f\d])$/i.exec(hex);
    if (shortResult) {
      return `rgb(${parseInt(shortResult[1] + shortResult[1], 16)}, ${parseInt(shortResult[2] + shortResult[2], 16)}, ${parseInt(shortResult[3] + shortResult[3], 16)})`;
    }
    return hex;
  }
}

// Initialize
function initHeaderGlass() {
  if (window.headerGlassInstance) {
    window.headerGlassInstance.stopOpacityWatcher();
    window.headerGlassInstance = null;
  }
  window.headerGlassInstance = new HeaderGlassEffect();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHeaderGlass);
} else {
  initHeaderGlass();
}

// Shopify editor support
document.addEventListener('shopify:section:load', initHeaderGlass);
document.addEventListener('shopify:section:reorder', initHeaderGlass);
